import HorizontalLine from '../ui/HorizontalLine';
import StyledFormSection from '../styled/StyledFormSection';
import MarginRateStyledInput from '../styled/MarginRateStyledInput';
import StyledSelect from '../styled/StyledSelect';
import { COUNTRIES, CURRENCIES } from '../../../../shared/CountriesData';
import { YesNoStyledRadioGroup } from '../styled/StyledRadioGroup';
import { Label } from '../ui/label';
import { BENEFIT_MARGIN_RATE_LABEL } from '@/constants/marginRateLabels';
import StyledTranslatableInput from '../styled/StyledTranslatableInput';
import TranslateIcon from '../translation/TranslateIcon';
import { Control, useFormContext, useWatch } from 'react-hook-form';
import { CarRentalFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { SupportedLocale } from '@/types/supported-locale.type';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { useChangeLabelValueWithSecondaryValue } from '@/hooks/useChangeLabelValueWithSecondaryValue';
import InsuranceList from './InsuranceList';
import OptionsList from './OptionsList';
import TaxesList from './TaxesList';
import OneWayFeesList from './OneWayFeesList';

interface CarRentalConfigurationProps {
  control: Control<CarRentalFormDataWithTranslations>;
  suppliers?: {
    id: string;
    name: string;
    currency: string;
  }[];
  language: SupportedLocale;
  isEditEnabled: boolean;
  translateFields: (fields?: Array<'benefit.name'>) => Promise<void>;
  isTranslating: boolean;
  errorsLength: number;
}

const CarRentalConfiguration = ({
  control,
  suppliers,
  isEditEnabled,
  language,
  translateFields,
  isTranslating,
  errorsLength,
}: CarRentalConfigurationProps) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const { setValue } = useFormContext<CarRentalFormDataWithTranslations>();

  const displayGlobalForm = useWatch({ control, name: 'displayGlobalForm' });

  const changeSupplierWithCurrency = useChangeLabelValueWithSecondaryValue({
    control,
    primaryName: 'supplier',
    secondaryName: 'currency',
    retrieveSecondary: (primaryValue) => {
      const supplier = suppliers?.find(
        (supplier) => supplier.id === primaryValue.value,
      );
      const currency = CURRENCIES.find(
        (currency) => currency.value === supplier?.currency,
      );
      return currency;
    },
  });

  const supplierOptions =
    suppliers?.map(({ id, name }) => ({ value: id, label: name })) ?? [];

  return (
    <>
      <StyledFormSection
        sectionTitle={t('forms.sections.generalInfo')}
        icon={
          <TranslateIcon
            translateFields={() => translateFields(['benefit.name'])}
            isTranslating={isTranslating}
            language={language}
            hidden={!isEditEnabled}
          />
        }
      >
        {errorsLength > 0 && (
          <Label className="text-error-500">
            {t('common.checkRequiredFields')}
          </Label>
        )}
        <StyledTranslatableInput
          control={control}
          name="benefit.name"
          originalName="name"
          setValue={setValue}
          language={language}
          title={t('carRental.name')}
          placeholder="Ex : Europcar"
          disabled={!isEditEnabled}
        />
        <div className="flex flex-col md:flex-row gap-4">
          <StyledSelect
            options={supplierOptions}
            control={control}
            name="supplier"
            title={t('carRental.supplier')}
            placeholder={t('forms.placeholders.selectSupplier')}
            isClearable
            disabled={!isEditEnabled}
            onChange={changeSupplierWithCurrency}
          />
          <StyledSelect
            options={CURRENCIES}
            control={control}
            name="currency"
            title={t('carRental.currency')}
            placeholder={t('forms.placeholders.selectCurrency')}
            disabled={!isEditEnabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <StyledSelect
            options={COUNTRIES}
            control={control}
            name="country"
            title={t('carRental.country') + ' *'}
            placeholder={t('forms.placeholders.selectCountry')}
            disabled={!isEditEnabled}
          />
          <MarginRateStyledInput
            control={control}
            name="marginRate"
            title={getTranslatedConst(BENEFIT_MARGIN_RATE_LABEL)}
            placeholder={t('forms.placeholders.addRate')}
            disabled={!isEditEnabled}
          />
        </div>
        {isEditEnabled && (
          <Label className="text-gray-700">
            {t('common.requiredFieldsNote')}
          </Label>
        )}
      </StyledFormSection>
      <HorizontalLine />
      <div className="flex flex-col md:flex-row gap-6 p-6">
        <h2 className="md:w-1/4 text-base font-medium text-black"></h2>
        <div className="flex flex-col gap-2 w-full lg:w-1/2 2xl:w-1/3">
          <div className="flex flex-col gap-1.5">
            <p className="text-sm leading-6 font-medium text-gray-700">
              {t('carRental.displayGlobalForm')}
            </p>
            <YesNoStyledRadioGroup
              control={control}
              name={'displayGlobalForm'}
              disabled={!isEditEnabled}
            />
          </div>
          {displayGlobalForm === 'true' && (
            <div className="flex flex-col gap-4 bg-brand-100 py-2 px-4 w-full">
              <p className="text-base text-black">
                {t('carRental.globalFormDescription')}
              </p>
            </div>
          )}
        </div>
      </div>
      {displayGlobalForm === 'true' && (
        <>
          <InsuranceList
            control={control}
            language={language}
            isViewMode={!isEditEnabled}
          />
          <HorizontalLine />
          <OptionsList
            control={control}
            language={language}
            isViewMode={!isEditEnabled}
          />
          <HorizontalLine />
          <TaxesList
            control={control}
            language={language}
            isViewMode={!isEditEnabled}
          />
          <HorizontalLine />
          <OneWayFeesList
            control={control}
            language={language}
            isViewMode={!isEditEnabled}
          />
          <HorizontalLine />
        </>
      )}
    </>
  );
};

export default CarRentalConfiguration;
